import React, {
  ReactElement, useCallback,
  useEffect, useMemo,
  useState,
} from 'react';
import {Entity} from '../../../types/entities/Entity';
import api from '../../../services/api';
import Dialog, {DialogProps} from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {SendModificationListItem} from '../../EntityDialog/Accommodation/SendModificationListItem';
import SendIcon from "@mui/icons-material/Send";
import {LoadingButton} from "@mui/lab";
import CircularProgress from "@mui/material/CircularProgress";
import {Alert} from "@mui/material";
import toast from "react-hot-toast";
import Paper, {PaperProps} from "@mui/material/Paper";
import Draggable from "react-draggable";
import {v4 as uuidv4} from "uuid";
import {DragIndicatorRounded} from "@mui/icons-material";

type Props<T extends Entity> = {
  onClose: (item: boolean) => void;
  onUpdatedRows?: (rows:any) => void;
  values: any;
  type: string;
};

export const AccommodationReservationModificationDialog = <T extends Entity>({
                                                                         type,
                                                                         values,
                                                                         onClose,
                                                                         onUpdatedRows,

                                                                       }: Props<T>): ReactElement => {
  const [modifications, setModifications] = useState<any>([]);
  const [maxWidth, setMaxWidth] = React.useState<DialogProps['maxWidth']>('md');
  const [checkedRows, setCheckedRows] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [messages, setMessages] = useState<Record<string, string>>({});

  const handleClose = () => {
    onClose(false);
  };

  const draggablePaneId = useMemo(() => {
    return `draggable-responsive-add-new-dialog-${uuidv4()}`;
  }, []);

  const fetchData = useCallback(() => {
    const typeForGet = type === 'service' ? '' : `-${type}`;

    setLoading(true);

    if (type === 'all') {
      const searchParams = new URLSearchParams({
        ids: values,
      });
      api
        .get(`/accommodation-reservation-modifications-all`, searchParams)
        .then((res: any) => {
          setModifications(res);

          const checkedRows: any = {};

          res.map((item: any) => {

            item.modifications.map((modification: any) => {
              checkedRows[modification?.id] = true;
            });

          });
          setCheckedRows(checkedRows);

        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      api
        .get(`/service-reservation-modifications${typeForGet}/${values?.id}`)
        .then((res: any) => {
          setModifications(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [type,values]);

  const handleSubmit = () => {
    setLoadingButton(true);

    // csak a kipipáltakhoz tartozó NEM üres üzenetek
    const messagesToSend = Object.fromEntries(
      Object.entries(messages)
        .filter(([id, val]) => checkedRows[id] && val?.trim())
    );

    api
      .post(`/send-accommodation-reservation-modifications`, {
        data: checkedRows,
        messages: messagesToSend // ÚJ: { [modificationId]: "szöveg" }
      })
      .then((res: any) => {

        if(res?.messages && res.messages.length){
          res.messages.map((message: any) => {
            if(message.type === 'success'){
              toast.success(message?.value)
            }else if(message.type === 'error'){
              toast.error(message?.value)
            }else{
              toast(message?.value, {
                icon: '✋',
              })
            }
          });
        }

        if (res?.updated_rows && onUpdatedRows){
          onUpdatedRows(res?.updated_rows);
        }

        handleClose();
      })
      .catch((error) => {
        toast.error('Error while send modifications');
      }).finally(() => {
      setLoadingButton(false);
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const PaperComponent = useCallback(
    (props: PaperProps) => {
      return (
        <Draggable
          handle={`#${draggablePaneId}, .drag-handle`}
          cancel={'[class*="MuiDialogContent-root"], .non-draggable-button'}
        >
          <Paper {...props} />
        </Draggable>
      );
    },
    []
  );

  return (
    <>
      <Dialog
        PaperComponent={PaperComponent}
        fullWidth={true}
        maxWidth={maxWidth}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={true}
      >
        <DialogTitle
          id={draggablePaneId}
          sx={{m: 0, p: 2}}>
          Modifications
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon/>
        </IconButton>
        <DialogContent dividers>

          {loading ? (
            <CircularProgress/>
          ) : (
            <>
              {!modifications.length && (
                <>
                  <Alert severity="info">There is no available modification</Alert>
                </>
              )}
              {modifications.map((item: any, index: number) => {
                return (
                  <SendModificationListItem
                    readonly={false}
                    item={item}
                    index={index}
                    onSubmit={() => {
                      fetchData();
                    }}
                    onChangeCheck={(id: any, value: any) => {
                      setCheckedRows({
                        ...checkedRows, [id]: value
                      });
                    }}
                    onChangeMessage={(id: string | number, text: string) => {
                      setMessages(prev => ({ ...prev, [id]: text }));
                    }}
                  />
                );
              })}
            </>
          )}


        </DialogContent>
        <DialogActions
          sx={{
            px: '30px'
          }}
        >
          <LoadingButton
            onClick={handleSubmit}
            loading={loadingButton}
            endIcon={<SendIcon color={'primary'}/>}
            disabled={!modifications.length}
          >
            Send To Provider
          </LoadingButton>
        </DialogActions>

        <IconButton
          sx={{
            borderTopLeftRadius: '0px',
            borderTopRightRadius: '20px',
            borderBottomRightRadius: '0px',
            borderBottomLeftRadius: '0px',
            position: 'absolute',
            bottom: 0,
            left: 0,
            background: '#0000000a'
          }}
          className="drag-handle" size="small" aria-label="Drag dialog">
          <DragIndicatorRounded fontSize={'small'} />
        </IconButton>

        <IconButton
          sx={{
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '0px',
            borderBottomRightRadius: '0px',
            borderBottomLeftRadius: '0px',
            position: 'absolute',
            bottom: 0,
            right: 0,
            background: '#0000000a'
          }}
          className="drag-handle" size="small" aria-label="Drag dialog">
          <DragIndicatorRounded fontSize={'small'} />
        </IconButton>

      </Dialog>

    </>
  );
};
